import { Dispatch } from "react";

import * as actionTypes from "./actionType";
import {
  fetchGamblingControl,
  fetchJSONData,
  updateGamblingControl,
} from "./api";
import { IApiHeaders, IAppProviderContextProps } from "../interfaces";

export const createActions = (
  dispatch: Dispatch<any>,
  state: IAppProviderContextProps,
  client: any
) => {
  const headers: IApiHeaders = {
    client,
    authToken: `Bearer-${state.startupData?.authToken}`,
  };

  const dispatchIsLoading = (isLoading: boolean) => {
    dispatch({
      type: actionTypes.IS_LOADING,
      payload: isLoading,
    });
  };

  /*
   * Handles errors by dispatching an error action with the error message.
   */
  const handleError = (error: any) => {
    const errorMessage: string =
      Array.isArray(error) && error?.length ? error[0].message : error;

    dispatch({
      type: actionTypes.SET_ERROR,
      payload: errorMessage,
    });
  };

  /*
   * Fetches JSON static content from a public folder JSON file.
   * Sets the result in the context state.
   */
  const getJSONContent = async () => {
    try {
      dispatchIsLoading(true);
      const jsonResponse = await fetchJSONData();
      dispatch({
        type: actionTypes.CMS_STATIC_CONTENT_SUCCESS,
        payload: jsonResponse.data,
      });
    } catch (error: any) {
      handleError(`fetch JSON Content failed: ${error.message}`);
    }
  };

  /*
   * Fetches selected card gambling control details along with static JSON content from a public folder.
   * Sets the result in the context state.
   */
  const getGamblingControlWithJson = async (
    tokenisedDebitCardNumber: string
  ) => {
    try {
      dispatchIsLoading(true);
      const [apiResponse, jsonResponse] = await Promise.all([
        fetchGamblingControl(headers, tokenisedDebitCardNumber),
        fetchJSONData(),
      ]);

      const { data, errors } = apiResponse;
      if (data?.debitCardControls?.merchantControls) {
        dispatch({
          type: actionTypes.GAMBLING_CONTROL_SUCCESS,
          payload: data.debitCardControls.merchantControls[0],
        });
      } else if (errors.length) {
        handleError(errors);
        return;
      }

      dispatch({
        type: actionTypes.CMS_STATIC_CONTENT_SUCCESS,
        payload: jsonResponse.data,
      });
    } catch (error: any) {
      handleError(`Get Gambling Control failed: ${error.message}`);
    }
  };

  /*
   * Updates selected card gambling control details by passing debitCardNumber and enable/disable flag.
   * Sets the result in the context state.
   */
  const setGamblingControl = async (
    tokenisedDebitCardNumber: string,
    declineAll: boolean
  ) => {
    try {
      dispatchIsLoading(true);
      const queryResponse = await updateGamblingControl(
        headers,
        tokenisedDebitCardNumber,
        declineAll
      );
      const { data, errors } = queryResponse;
      if (data?.setDebitCardControls?.merchantControls?.length) {
        dispatch({
          type: actionTypes.GAMBLING_CONTROL_SUCCESS,
          payload: data.setDebitCardControls.merchantControls[0],
        });
      } else if (errors.length) {
        handleError(errors);
      }
    } catch (error: any) {
      handleError(`Set Gambling Control failed: ${error.message}`);
    }
  };

  return {
    getJSONContent,
    getGamblingControlWithJson,
    setGamblingControl,
  };
};
